/// <amd-module name="DRITools/DRIToolsHub/Callgraph/CallgraphTable" />
import * as React from "react";
import d3 = require("d3");

// VSS
import { ObservableLike, ObservableValue } from "VSS/Core/Observable";

// VSSUI
import { Link } from "VSSUI/Link";
import { renderListCell } from "VSSUI/List";
import { Icon, IconSize } from "VSSUI/Icon";
import { SimpleTableCell, ISimpleTableCell } from "VSSUI/Table";
import { ExpandableTreeCell, Tree, ITreeColumn, ITreeRow } from "VSSUI/TreeEx";
import { ITreeItemEx, ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

// DRITools
import { Obj, RequestNode } from "DRITools/Core/Contracts/DRITools";
import * as Resources from "DRITools/DRIToolsHub/Resources";

export interface ICallgraphTableProps {
    endTime: Date;
    maxDepth: number;
    requests: RequestNode[];
    startTime: Date;
    width: number;
    onSelectItem: (node: RequestNode) => void;
    onShowSlowTraces: (traces: Obj[]) => void;
}

interface ICallgraphTableItem extends ISimpleTableCell {
    activityLog: Obj;
    requestNode: Obj;
    hasSlowTraces: number;
}

interface Column {
    id: string;
    isDate?: boolean;
    name: string;
    width: number;
}

export class CallgraphTable extends React.Component<ICallgraphTableProps> {
    // Color scale for gradient
    private colorScale = d3
        .scaleLinear<String>()
        .domain([0, 5000, 10000, Number.MAX_SAFE_INTEGER])
        .range([
            "#55a362", // green
            "#d67f3c", // yellow
            "#cd4a45", // red
            "#cd4a45"  // red after threshold
        ]);
    // Table columns with width
    private columnNames: Column[] = [
        { id: "ScaleUnit", name: Resources.Callgraph_ColumnName_ScaleUnit, width: 100 },
        { id: "ActivityId", name: Resources.Callgraph_ColumnName_ActivityId, width: 175 },
        { id: "StartTime", isDate: true, name: Resources.Callgraph_ColumnName_StartTime, width: 180 },
        { id: "Command", name: Resources.Callgraph_ColumnName_Command, width: 270 },
        { id: "ResponseCode", name: Resources.Callgraph_ColumnName_ResponseCode, width: 90 },
        { id: "ActivityStatus", name: Resources.Callgraph_ColumnName_ActivityStatus, width: 60 }
    ]
    // Date to string options
    private dateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
    private myRef: HTMLDivElement[];

    constructor(props: ICallgraphTableProps) {
        super(props);

        this.columnNames.unshift({ id: "Service", name: Resources.Callgraph_ColumnName_Service, width: 70 + 24 * this.props.maxDepth });
        this.myRef = [];
    }

    public render(): JSX.Element {
        const itemProvider = new TreeItemProvider<ICallgraphTableItem>(this.props.requests.map((value) => {
            return this.convertRequestItems(value);
        }));

        return (
            <div className="waterfall-list">
                <Tree<ICallgraphTableItem>
                    ariaLabel={Resources.Callgraph_Table_AriaLabel}
                    columns={this.getColumns()}
                    itemProvider={itemProvider}
                    selectableText={true}
                    singleClickActivation={false}
                    scrollable={true}
                    onSelect={(event, treeRow: ITreeRow<ICallgraphTableItem>) => {
                        this.props.onSelectItem(treeRow.data.underlyingItem.data.requestNode.self);
                    }}
                    onToggle={(event, treeItem: ITreeItemEx<ICallgraphTableItem>) => {
                        itemProvider.toggle(treeItem.underlyingItem);
                    }}
                />
            </div>
        );
    }

    private convertRequestItems = (node: RequestNode): ITreeItem<ICallgraphTableItem> => {
        const requestNode: Obj = {
            self: node
        }

        return {
            childItems: node.childItems.length > 0 ? node.childItems.map((value) => this.convertRequestItems(value)) : undefined,
            data: { activityLog: node.data.activityLog, hasSlowTraces: node.data.slowTraces.length, requestNode },
            expanded: node.childItems.length > 0
        }
    }

    private getColumns(): ITreeColumn<ICallgraphTableItem>[] {
        const columns: ITreeColumn<ICallgraphTableItem>[] = [];
        let waterfallWidth = this.props.width - 40 - 40; //40 is slow trace, 40 is padding around svg

        // If width isn't specified in column object, equaly distribute width
        this.columnNames.forEach((column, index) => {
            waterfallWidth -= column.width;
            columns.push({
                id: column.id,
                name: column.name,
                renderCell: (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ICallgraphTableItem>, treeItem: ITreeItemEx<ICallgraphTableItem>) => {
                    return this.renderColumnCell(columnIndex, treeColumn, treeItem, index === 0 && this.props.maxDepth > 1, column.isDate);
                },
                width: new ObservableValue(column.width)
            })
        });

        if (waterfallWidth < 600) {
            waterfallWidth = 600;
        }
        const totalTime = (this.props.endTime.getTime() - this.props.startTime.getTime());
        const scaleFactor: number = (waterfallWidth - 20) / totalTime;

        // timeline lines separated into 8 parts - 7 lines
        let timeLines: number[] = [];
        for (let i = 1; i < 9; i++) {
            timeLines.push(totalTime / 8 * i);
        }

        columns.push({
            id: "SlowTrace",
            iconProps: { tooltipProps: { text: Resources.Callgraph_AriaLabel_SlowTraceButton }, iconName: "RawSource", size: IconSize.small },
            name: "",
            renderCell: (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ICallgraphTableItem>, treeItem: ITreeItemEx<ICallgraphTableItem>) => {
                return (<SimpleTableCell
                    columnIndex={columnIndex}
                    tableColumn={treeColumn}
                    key={"col-" + columnIndex}
                    contentClassName="fontWeightSemiBold font-weight-semibold fontSizeM font-size-m scroll-hidden"
                >
                    {
                        treeItem.underlyingItem.data.hasSlowTraces > 0 &&
                        <Link ariaLabel={Resources.Callgraph_AriaLabel_SlowTraceButton} href="#" role="button" onClick={() => {
                            this.props.onShowSlowTraces(treeItem.underlyingItem.data.requestNode.self.data.slowTraces)
                        }}>
                            <Icon iconName="Clock" size={IconSize.small} />
                        </Link>
                    }
                </SimpleTableCell>)
            },
            width: new ObservableValue(40)
        }, {
            id: "Waterfall",
            name: "WaterFall",//Not visible
            renderHeaderCell: (columnIndex: number, treeColumn: ITreeColumn<ICallgraphTableItem>, focuszoneId: string | undefined) => {
                return (<th
                    aria-colindex={columnIndex + 1}
                    aria-label={treeColumn.ariaLabel}
                    className="bolt-table-header-cell bolt-table-header-cell-empty"
                    data-column-index={columnIndex}
                    key="col-waterfall"
                >
                    {this.getWaterfallHeader(
                        scaleFactor,
                        waterfallWidth,
                        timeLines
                    )}
                </th>)
            },
            renderCell: (rowIndex: number, columnIndex: number, treeColumn: ITreeColumn<ICallgraphTableItem>, treeItem: ITreeItemEx<ICallgraphTableItem>) => {
                return (<SimpleTableCell
                    columnIndex={columnIndex}
                    tableColumn={treeColumn}
                    key={"col-" + columnIndex}
                    contentClassName="fontWeightSemiBold font-weight-semibold fontSizeM font-size-m scroll-hidden"
                >
                    {this.getWaterfallCell(
                        treeItem.underlyingItem.data.activityLog,
                        scaleFactor,
                        waterfallWidth,
                        timeLines,
                        (treeItem.underlyingItem.data.activityLog.StartTime.getTime() - this.props.startTime.getTime()) / totalTime > 0.9
                    )}
                </SimpleTableCell>)
            },
            width: new ObservableValue(-100)
        })

        return columns;
    }

    // Draw waterfall bars
    private getWaterfallCell(activityLog: Obj, scaleFactor: number, width: number, timeLines: number[], closeToEnd: boolean) {
        const gradient = {
            fill: this.colorScale(activityLog.ExecutionTime / 1000)
        } as React.CSSProperties;
        const transform = (activityLog.StartTime.getTime() - this.props.startTime.getTime()) * scaleFactor;

        return (
            <div className="waterfall-cell scroll-hidden" ref={(ref) => {
                if (ref) {
                    this.myRef.push(ref);
                }
            }}>
                <svg width={width} height={40}>
                    <g transform={`translate(${transform}, 0)`}>
                        <rect
                            height="100%"
                            style={gradient}
                            width={(activityLog.ExecutionTime / 1000) * scaleFactor}
                        />
                    </g>
                    {timeLines.map((value, index) => {
                        return <line key={"waterfall-line" + index} x1={value * scaleFactor} y1="0" x2={value * scaleFactor} y2="40" />
                    })}
                    <g transform={`translate(${closeToEnd ? (-0.1 * width) + transform : transform}, 0)`}>
                        <text x="10" y={25} >
                            {Math.floor(activityLog.ExecutionTime / 1000) + " ms"}
                        </text>
                    </g>
                </svg>
            </div>
        );
    }

    // Draw waterfall header
    private getWaterfallHeader(scaleFactor: number, width: number, timeLines: number[]) {
        return (
            <div className="bolt-table-header-cell-content waterfall-header" ref={(ref) => {
                if (ref) {
                    ref.addEventListener("scroll", (event: Event) => {
                        this.handleScroll((event.currentTarget as any).scrollLeft);
                    })
                    this.myRef.push(ref);
                }
            }}>
                <svg width={width} height={20}>
                    {timeLines.map((value, index) => {
                        return <g key={"waterfall-header-line" + index} transform={`translate(${value * scaleFactor}, 0)`}>
                            <line x1="0" y1="0" x2="0" y2="20" />
                            <text className="bolt-table-header-cell-text body-s" x="-5" y="15" direction="rtl">
                                {Math.floor(value) + "ms"}
                            </text>
                        </g>
                    })}
                </svg>
            </div>
        );
    }

    private renderColumnCell = (columnIndex: number, treeColumn: ITreeColumn<ICallgraphTableItem>, treeItem: ITreeItemEx<ICallgraphTableItem>, isExpandable: boolean, isDate: boolean = false) => {
        const { underlyingItem } = treeItem;
        const data = ObservableLike.getValue(underlyingItem.data);
        let treeCell = data && data.activityLog && data.activityLog[treeColumn.id];

        if (isDate) {
            treeCell = treeCell.toLocaleDateString('en-US', this.dateFormatOptions);
        }

        // Do not include padding if the table cell has an href
        const hasLink = !!(treeCell && typeof treeCell !== "string" && typeof treeCell !== "number" && treeCell.href);
        const cellProps = {
            children: treeCell && renderListCell(treeCell),
            className: treeColumn.className,
            columnIndex,
            contentClassName: hasLink ? "bolt-table-cell-content-with-link" : undefined,
        }

        if (isExpandable) {
            return ExpandableTreeCell({ ...cellProps, treeItem, treeColumn });
        };

        return SimpleTableCell({ ...cellProps, tableColumn: treeColumn });
    }

    private handleScroll = (scrollLeft: number) => {
        this.myRef.forEach((ref) => {
            ref.scrollLeft = scrollLeft;
        })
    }
}

export default CallgraphTable;
